import get from 'lodash-es/get';
import isNil from 'lodash-es/isNil';
import moment from 'moment';

import store from 'app/store/index';
import { FormsConfigActions } from 'app/store/data/formsConfig';
import { isInValidRange } from 'app/validators/validateDate';
import {
  changeFieldValueAndUpdate,
  clearFieldValue,
  clearHiddenDictionaryEntries,
  getCarData,
  getDictionaryFromSelector,
  getFirstCoownerData,
  getFormValue,
  getFormValues,
  getInsuranceData,
  getIsMotorFromSelector,
  getOwnerData,
  getQuotation,
  getSecondCoownerData,
  getUserData,
  setDictionaryEntriesVisibility,
  setDictionaryEntryVisibility,
  setInitialFocusedDate,
  setMaxDate,
  setMessageVisibility,
  setMinDate,
  setQuestionAvailability,
  setQuestionHelperText,
  setQuestionLabel,
  setQuestionsVisibility,
  setQuestionVisibility,
  setSectionsVisibilities,
  setSectionVisibilityHelper,
  getQuestion,
  getQuotationIsFromRenewal,
  changeQuestionClearOnHide,
  getIsDataOwnersChangedRenewal,
  setDataCarChangedRenewal,
  getCepikDataFromSelector,
  setMessage,
  changeFieldValue,
  setQuestionRequirability,
  updateFormConfigWithConsentLabels,
  updateFormConfigWithMjmConsent,
  getIsCepik3PathActive,
} from 'app/services/businessFunctionHelpers';
import { getIsMubiLeasingConfig } from 'app/shared/helpers/getIsMubiLeasingConfig';
import {
  ControlLabelEnum,
  LeasingVehicleTypeEnum,
  DictionariesEnum,
  DriverType,
  FieldsEnum,
  FormsEnum,
  InsuranceTypeEnum,
  MessagesEnum,
  ModelPathEnum,
  SectionsEnum,
  VehicleTypeEnum,
  WayOfUseEnum,
  MessageTypeEnum,
  QuestionMessagesEnum,
  RouteEnum,
  LabelEnum,
} from 'app/shared/enums';
import { FormConfig } from 'app/shared/interfaces/FormConfig.interface';
import { mubiLeasingFormConfig } from 'app/shared/configs';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';
import { owner } from 'app/services/businessFunctions/calculation/owner';
import { firstCoowner } from 'app/services/businessFunctions/calculation/firstCoowner';
import { secondCoowner } from 'app/services/businessFunctions/calculation/secondCoowner';
import { car } from 'app/services/businessFunctions/calculation/car';
import { insurant } from 'app/services/businessFunctions/calculation/insurant';
import { checkout } from 'app/services/businessFunctions/checkout';
import { consents } from 'app/services/businessFunctions/calculation/consents';
import { completeData } from 'app/services/businessFunctions/completeData';
import { operatorLead } from 'app/services/businessFunctions/operatorLead';
import { travel } from 'app/services/businessFunctions/travel';
import { travelCheckout } from 'app/services/businessFunctions/travelCheckout';
import { terminationGenerator } from 'app/services/businessFunctions/terminationGenerator';
import { skpRegistration } from 'app/services/businessFunctions/skpRegistration';
import { isMubiLeasingActive } from 'app/services/env.service';
import { getMessageKeyWithSuffix } from './validation.service';
import { revalidatePeselsUniqueness } from './pesel.service/revalidatePeselsUniqueness';
import {
  getShouldHideDrivingWheelLocation,
  setDrivingWheelLocationVisibility,
} from 'app/services/businessFunctions/helpers/setDrivingWheelLocationVisibility';
import { setMainOwnerSelectionQuestionVisibility } from 'app/services/businessFunctions/helpers/setMainOwnerSelectionQuestionVisibility';
import { firstStep } from './businessFunctions/calculation/firstStep';

const DATE_FORMAT = 'YYYY-MM-DD';

function getMaxCarRegistrationDate(productionYear: number) {
  const maxRegistrationDelay = 3;
  const currentYear = moment().year();
  const maxFirstRegistrationYear = productionYear + maxRegistrationDelay;
  return maxFirstRegistrationYear >= currentYear
    ? moment().format(DATE_FORMAT)
    : `${maxFirstRegistrationYear}-12-31`;
}

function isCarOlderThan(age: number): boolean {
  const carProductionYear = getCarData().productionYear;
  const currentYear = moment().year();
  const carAge = currentYear - carProductionYear;

  return carAge > age;
}

export function setupLicenseDates(
  driverLicenseMonthQuestionKey: FieldsEnum,
  driverLicenseYearQuestionKey: FieldsEnum,
  birthDateQuestionKey: FieldsEnum,
  noDrivingLicenseQuestionKey: FieldsEnum | undefined,
  formName: FormsEnum = FormsEnum.CalculationForm,
  ignoreDriverLicenseYearCheck: boolean = false
) {
  const birthDate = get(getFormValues(formName), birthDateQuestionKey);

  if (!birthDate) {
    setMessageVisibility({
      formName,
      key: getMessageKeyWithSuffix(birthDateQuestionKey, 'invalid_date'),
      visible: false,
    });
    setMessageVisibility({
      formName,
      key: getMessageKeyWithSuffix(birthDateQuestionKey, 'too_young_owner'),
      visible: false,
    });
  }

  const driverLicenseYear = get(
    getFormValues(formName),
    driverLicenseYearQuestionKey
  );
  const noDrivingLicense = noDrivingLicenseQuestionKey
    ? get(getFormValues(formName), noDrivingLicenseQuestionKey)
    : undefined;
  const currentDate = moment();

  const yearsDiff = currentDate.diff(moment(birthDate), 'years');

  if (noDrivingLicense) {
    setQuestionVisibility({
      key: driverLicenseMonthQuestionKey,
      visible: false,
      formName,
    });
    setQuestionAvailability({
      key: driverLicenseYearQuestionKey,
      enabled: false,
      formName,
    });
    if (formName !== FormsEnum.CompareCepikDataForm)
      clearFieldValue(driverLicenseYearQuestionKey, formName);
  } else {
    const isAdult = yearsDiff >= 17;

    const { minDate, maxDate } = getQuestion(birthDateQuestionKey, formName);

    const isValidBirthDate =
      birthDate && isInValidRange(minDate, maxDate, birthDate);

    setQuestionAvailability({
      key: driverLicenseYearQuestionKey,
      enabled: isAdult && isValidBirthDate,
      formName,
    });
    setQuestionVisibility({
      key: driverLicenseMonthQuestionKey,
      visible:
        isAdult &&
        isValidBirthDate &&
        (ignoreDriverLicenseYearCheck ||
          driverLicenseYear >= currentDate.year() - 4),
      formName,
    });
  }
}

export function setLicenseMonthVisibility(
  driverBirthDayQuestionKey: FieldsEnum,
  driverLicenseYearQuestionKey: FieldsEnum,
  driverLicenseMonthQuestionKey: FieldsEnum,
  noDrivingLicenseQuestionKey: FieldsEnum | undefined,
  formName: FormsEnum = FormsEnum.CalculationForm,
  shouldLimitMonthsToLast3Years: boolean = false
) {
  const driverLicenseYear = get(
    getFormValues(formName),
    driverLicenseYearQuestionKey
  );
  const currentDate = moment();
  const noDrivingLicense = noDrivingLicenseQuestionKey
    ? get(getFormValues(formName), noDrivingLicenseQuestionKey)
    : undefined;
  const isDriverLicenseMonthVisible =
    driverLicenseYear >= currentDate.year() - 4 && !noDrivingLicense;
  const months = [
    'JANUARY',
    'FEBRUARY',
    'MARCH',
    'APRIL',
    'MAY',
    'JUNE',
    'JULY',
    'AUGUST',
    'SEPTEMBER',
    'OCTOBER',
    'NOVEMBER',
    'DECEMBER',
  ];
  const driverBirthDay = get(
    getFormValues(formName),
    driverBirthDayQuestionKey
  );
  const momentDriverBirthDay = moment(driverBirthDay, 'YYYY-MM-DD', true);

  const limitMonthsToLast3Years = () => {
    const thresholdYear = currentDate.year() - 3;
    if (driverLicenseYear <= thresholdYear) {
      const allowedMonths = months.slice(currentDate.month());
      setDictionaryEntriesVisibility({
        dictionaryKey: `months_${driverLicenseMonthQuestionKey}`,
        entriesKey: months.filter((m) => !allowedMonths.includes(m)),
        visible: false,
        formName,
      });
    }
  };

  const hideMonthsBeforeAdulthood = () => {
    const hasDriverLicenseYearWhen17YearsOld =
      driverBirthDay &&
      momentDriverBirthDay.year() + 17 === Number(driverLicenseYear);

    if (hasDriverLicenseYearWhen17YearsOld) {
      const monthEntries = months.slice(0, momentDriverBirthDay.month());
      setDictionaryEntriesVisibility({
        dictionaryKey: `months_${driverLicenseMonthQuestionKey}`,
        entriesKey: monthEntries,
        visible: false,
        formName,
      });
    }
  };

  clearHiddenDictionaryEntries({
    dictionaryKey: `months_${driverLicenseMonthQuestionKey}`,
    formName,
  });

  if (
    driverLicenseYear &&
    driverLicenseYear.toString() === currentDate.year().toString()
  ) {
    const entriesKey = months.slice(currentDate.month() + 1);
    const driverLicenseMonthValue = getFormValue(
      driverLicenseMonthQuestionKey,
      formName
    );

    if (
      entriesKey.includes(driverLicenseMonthValue) &&
      formName !== FormsEnum.CompareCepikDataForm
    ) {
      changeFieldValueAndUpdate(driverLicenseMonthQuestionKey, null, formName);
    }

    setDictionaryEntriesVisibility({
      dictionaryKey: `months_${driverLicenseMonthQuestionKey}`,
      entriesKey,
      visible: false,
      formName,
    });
  }
  hideMonthsBeforeAdulthood();

  if (shouldLimitMonthsToLast3Years) limitMonthsToLast3Years();

  if (
    !isDriverLicenseMonthVisible &&
    formName !== FormsEnum.CompareCepikDataForm
  ) {
    changeFieldValueAndUpdate(driverLicenseMonthQuestionKey, null, formName);
  }

  setQuestionVisibility({
    key: driverLicenseMonthQuestionKey,
    visible: isDriverLicenseMonthVisible,
    formName,
  });
}

export function setupNoDrivingLicenseFieldVisibility(
  noDrivingLicenseQuestionKey: FieldsEnum,
  noDrivingLicenseQuestionModelPath: ModelPathEnum | string,
  driverLicenseYear: FieldsEnum,
  birthDateQuestionKey: FieldsEnum,
  initialRun: boolean,
  formName: FormsEnum = FormsEnum.CalculationForm,
  clearLicenseYearOnDisable: boolean = false
) {
  const quotation = getQuotation();
  const birthDate = get(getFormValues(formName), birthDateQuestionKey);
  const noDrivingLicense = get(
    getFormValues(formName),
    noDrivingLicenseQuestionKey
  );
  const currentDate = moment();
  const yearsDiff = currentDate.diff(moment(birthDate), 'years');
  const noDriverLicenseQuotationValue = get(
    quotation,
    noDrivingLicenseQuestionModelPath
  );
  const isFromRenewal = getQuotationIsFromRenewal();

  const isNoDrivingLicenseValueChanged =
    noDrivingLicense !== noDriverLicenseQuotationValue;

  const isNoDrivingLicenseAlreadyKnown =
    formName === FormsEnum.CompareCepikDataForm &&
    !isNil(noDriverLicenseQuotationValue) &&
    !isNoDrivingLicenseValueChanged;

  const { minDate, maxDate } = getQuestion(birthDateQuestionKey, formName);

  const isValidBirthDate =
    birthDate && isInValidRange(minDate, maxDate, birthDate);

  const shouldClearNoDrivingLicenseFieldValue =
    !initialRun &&
    !isNoDrivingLicenseValueChanged &&
    !isNoDrivingLicenseAlreadyKnown &&
    yearsDiff >= 17;

  const ownerName = {
    [FieldsEnum.OwnerNoDrivingLicense]: 'owner',
    [FieldsEnum.Coowners1NoDrivingLicense]: 'firstCoOwner',
    [FieldsEnum.Coowners2NoDrivingLicense]: 'secondCoOwner',
  };

  const driverLicenseYearEnabled =
    isValidBirthDate &&
    (!noDrivingLicense || shouldClearNoDrivingLicenseFieldValue);

  setQuestionAvailability({
    key: driverLicenseYear,
    enabled: driverLicenseYearEnabled,
    formName,
  });

  if (clearLicenseYearOnDisable && !driverLicenseYearEnabled) {
    changeFieldValueAndUpdate(driverLicenseYear, null, formName);
  }

  if (shouldClearNoDrivingLicenseFieldValue) {
    changeFieldValueAndUpdate(noDrivingLicenseQuestionKey, null, formName);
  }

  if (yearsDiff >= 17) {
    setQuestionsVisibility(
      [noDrivingLicenseQuestionKey, driverLicenseYear],
      isNoDrivingLicenseAlreadyKnown ||
        (isFromRenewal &&
          !getIsDataOwnersChangedRenewal(
            ownerName[noDrivingLicenseQuestionKey]
          ))
        ? false
        : true,
      formName
    );
  } else {
    setQuestionsVisibility(
      [noDrivingLicenseQuestionKey, driverLicenseYear],
      false,
      formName
    );

    if (isValidBirthDate) {
      changeFieldValueAndUpdate(noDrivingLicenseQuestionKey, true, formName);
    }
  }
}

function showRegistrationDateMessages() {
  const { productionYear, firstRegistrationDate } = getCarData();
  const firstRegDate = moment(firstRegistrationDate, DATE_FORMAT);
  const messagesVisibility = [
    {
      key: MessagesEnum.FirstRegistrationDateCantBeInFuture,
      visible: firstRegDate.isAfter(moment(), 'day'),
    },
    {
      key: MessagesEnum.FirstRegistrationDateCantBeBeforeProdYear,
      visible: firstRegDate.isBefore(`${productionYear}-01-01`, 'year'),
    },
    {
      key: MessagesEnum.FirstRegistrationDateCantBeToLate,
      visible: firstRegDate.diff(`${productionYear}-01-01`, 'years') > 3,
    },
  ];

  messagesVisibility.forEach(({ key, visible }) =>
    setMessageVisibility({
      key,
      visible,
      formName: FormsEnum.CalculationForm,
    })
  );
}

export const BusinessFunctionsService = {
  ...owner,
  ...firstCoowner,
  ...secondCoowner,
  ...car,
  ...consents,
  ...checkout,
  ...insurant,
  ...completeData,
  ...operatorLead,
  ...travel,
  ...travelCheckout,
  ...terminationGenerator,
  ...skpRegistration,
  ...firstStep,

  setupAutocascoAvailability() {
    const carProductionYear = getCarData().productionYear;
    const isAutocascoAvailable =
      !isCarOlderThan(20) || isNil(carProductionYear);
    const selectedInsuranceScope = getFormValue(FieldsEnum.InsuranceScope);
    const dictionaryKey = `${DictionariesEnum.CarInsuranceScopes}_${FieldsEnum.InsuranceScope}`;

    setDictionaryEntryVisibility({
      dictionaryKey,
      entryKey: InsuranceTypeEnum.Ac,
      visible: isAutocascoAvailable,
      formName: FormsEnum.CalculationForm,
    });

    if (!isAutocascoAvailable) {
      const updatedInsuranceScope = selectedInsuranceScope.filter(
        (item: InsuranceTypeEnum) => item !== InsuranceTypeEnum.Ac
      );

      changeFieldValueAndUpdate(
        FieldsEnum.InsuranceScope,
        updatedInsuranceScope
      );
    }
  },

  onProductionYearChange(initialRun?: boolean) {
    const { productionYear } = getCarData();
    const { id: quotationId } = getQuotation();
    const isCepik3PathActive = getIsCepik3PathActive();

    if (productionYear) {
      const formsConfigState = store.getState().formsConfig;
      const vehicleTypeChanged = formsConfigState.vehicleTypeChanged;
      const prevProductionYear =
        formsConfigState.previousProductionYear &&
        formsConfigState.previousProductionYear[quotationId];

      if (
        !vehicleTypeChanged &&
        prevProductionYear &&
        prevProductionYear !== productionYear
      ) {
        clearFieldValue(
          FieldsEnum.CarFirstRegistrationDate,
          FormsEnum.CalculationForm,
          false
        );
      }

      store.dispatch(FormsConfigActions.setVehicleTypeChanged(false));

      store.dispatch(
        FormsConfigActions.setPreviousProductionYear({
          [quotationId]: productionYear,
        })
      );

      if (isCepik3PathActive) return;

      setMinDate({
        key: FieldsEnum.CarFirstRegistrationDate,
        minDate: `${productionYear}-01-01`,
        formName: FormsEnum.CalculationForm,
      });
      setInitialFocusedDate({
        key: FieldsEnum.CarFirstRegistrationDate,
        initialFocusedDate: `${productionYear}-01-15`,
        formName: FormsEnum.CalculationForm,
      });
      setMaxDate({
        key: FieldsEnum.CarFirstRegistrationDate,
        maxDate: `${getMaxCarRegistrationDate(productionYear)}`,
        formName: FormsEnum.CalculationForm,
      });

      setDrivingWheelLocationVisibility();
    }
  },

  wayOfUseChange() {
    const wayOfUse = getFormValues().plannedUsage.wayOfUse;
    const isOperator = getUserData().user?.operator;
    const quotation = getQuotation();
    const isRenewal = !!(quotation.renewal && !quotation.externalRenewal);

    const isCalculationAllowed = [
      WayOfUseEnum.Private,
      WayOfUseEnum.SoleTrader,
    ].includes(wayOfUse);

    setMessageVisibility({
      key: MessagesEnum.ContactUsMessage,
      visible: !isCalculationAllowed,
      formName: FormsEnum.CalculationForm,
    });

    setQuestionVisibility({
      key: FieldsEnum.CarVin,
      visible:
        (isOperator || isRenewal) && wayOfUse !== WayOfUseEnum.SoleTrader,
      formName: FormsEnum.CalculationForm,
    });
  },

  firstRegistrationCountryChange(initialRun?: boolean) {
    const isMotor = getIsMotorFromSelector();
    const isFromRenewal = getQuotationIsFromRenewal();
    const shouldHideDrivingWheelLocation = getShouldHideDrivingWheelLocation();

    const {
      firstRegistrationCountry,
      firstRegistrationDate,
      ownerPurchaseYear,
      productionYear,
    } = getCarData();

    if (isMotor || !ownerPurchaseYear) {
      return;
    }

    const firstRegistrationDateYear = moment(firstRegistrationDate).year();
    const carOwnerPurchaseYear = Number(ownerPurchaseYear);
    const showCarDrivingWheelLocationFor = ['eur', 'other'];

    const isCarDrivingWheelLocationVisible =
      showCarDrivingWheelLocationFor.includes(firstRegistrationCountry) &&
      !shouldHideDrivingWheelLocation &&
      Boolean(
        firstRegistrationDateYear &&
          carOwnerPurchaseYear &&
          firstRegistrationDateYear !== carOwnerPurchaseYear
      );

    const todayYear = new Date().getFullYear();
    const isCarOlderThan20 = productionYear && todayYear - productionYear > 20;

    if (isFromRenewal && !initialRun) {
      setDataCarChangedRenewal({
        field: 'firstRegistrationCountry',
        isChanged: true,
      });
    }

    if (isFromRenewal && isCarOlderThan20) {
      changeQuestionClearOnHide(
        FieldsEnum.CarFirstRegistrationCountry,
        FormsEnum.CalculationForm,
        false
      );
      changeQuestionClearOnHide(
        FieldsEnum.CarDrivingWheelLocation,
        FormsEnum.CalculationForm,
        false
      );
      setQuestionVisibility({
        key: FieldsEnum.CarDrivingWheelLocation,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
      setQuestionVisibility({
        key: FieldsEnum.CarFirstRegistrationCountry,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
    } else {
      setQuestionVisibility({
        key: FieldsEnum.CarDrivingWheelLocation,
        visible: isCarDrivingWheelLocationVisible,
        formName: FormsEnum.CalculationForm,
      });
    }

    if (initialRun || !isCarDrivingWheelLocationVisible) {
      return;
    }

    const dictionary = getDictionaryFromSelector(
      FormsEnum.CalculationForm,
      `${FieldsEnum.CarDrivingWheelLocation}_dictionary`
    );

    if (!dictionary) {
      return;
    }

    const defaultSelectedEntry = dictionary.find((entry) =>
      Boolean(entry.selected)
    );

    if (!defaultSelectedEntry) {
      return;
    }

    changeFieldValueAndUpdate(
      FieldsEnum.CarDrivingWheelLocation,
      defaultSelectedEntry.key
    );
  },

  setCarIsImportedInTheLast12MonthsVisibility() {
    const isMotor = getIsMotorFromSelector();

    if (isMotor) {
      return;
    }

    const firstRegistrationCountry = getCarData().firstRegistrationCountry;
    const ownerPurchaseYear = +getCarData().ownerPurchaseYear;
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;
    const carPurchasedInCurrentOrLastYear =
      ownerPurchaseYear === currentYear || ownerPurchaseYear === lastYear;
    const visible =
      firstRegistrationCountry !== 'pol' && carPurchasedInCurrentOrLastYear;
    const isImportedInTheLast12MonthsValue = getFormValue(
      FieldsEnum.CarIsImportedInTheLast12Months
    );

    setQuestionVisibility({
      key: FieldsEnum.CarIsImportedInTheLast12Months,
      visible,
      formName: FormsEnum.CalculationForm,
    });

    if (visible && isNil(isImportedInTheLast12MonthsValue)) {
      changeFieldValueAndUpdate(
        FieldsEnum.CarIsImportedInTheLast12Months,
        false
      );
    }
  },

  coownersCountChange(initialRun: boolean) {
    const coownersCount = getCarData().coownersCount;

    setMainOwnerSelectionQuestionVisibility();

    const coownerSections = [
      {
        key: SectionsEnum.Coowners1,
        visible: false,
        formName: FormsEnum.CalculationForm,
      },
      {
        key: SectionsEnum.Coowners2,
        visible: false,
        formName: FormsEnum.CalculationForm,
      },
    ];
    const coownersInsuranceSections = [
      {
        key: SectionsEnum.Coowners1InsuranceHistory,
        visible: false,
        formName: FormsEnum.CalculationForm,
      },
      {
        key: SectionsEnum.Coowners2InsuranceHistory,
        visible: false,
        formName: FormsEnum.CalculationForm,
      },
    ];

    switch (coownersCount) {
      case '0':
        coownerSections[0].visible = false;
        coownerSections[1].visible = false;
        coownersInsuranceSections[0].visible = false;
        coownersInsuranceSections[1].visible = false;
        break;
      case '1':
        coownerSections[0].visible = true;
        coownerSections[1].visible = false;
        coownersInsuranceSections[0].visible = true;
        coownersInsuranceSections[1].visible = false;
        break;
      case '2_':
        coownerSections[0].visible = true;
        coownerSections[1].visible = true;
        coownersInsuranceSections[0].visible = true;
        coownersInsuranceSections[1].visible = true;
        break;
      default:
        coownerSections[0].visible = false;
        coownerSections[1].visible = false;
        coownersInsuranceSections[0].visible = false;
        coownersInsuranceSections[1].visible = false;
    }
    setSectionsVisibilities(coownerSections);

    if (coownersCount !== '0') {
      BusinessFunctionsService.setupFirstCoownerLicenseDates(initialRun);
      firstCoowner.firstCoownerPeselChange(true);
      firstCoowner.calculationShowCoownersPeselObfuscatedMessage();
      firstCoowner.setFirstCoownerHistoryVisibilityBasedOnPersonalData();
    }
    setSectionsVisibilities(coownersInsuranceSections);
    BusinessFunctionsService.onShowMorePersonalDataChange(initialRun);

    revalidatePeselsUniqueness(FormsEnum.CalculationForm);
  },

  additionalDriversCountChange() {
    const hasAdditionalDrivers = getCarData().hasAdditionalDrivers;

    if (hasAdditionalDrivers) {
      const currentDate = new Date();
      const minDate = moment(currentDate)
        .subtract(26, 'years')
        .format('YYYY-MM-DD');

      setMinDate({
        key: FieldsEnum.AdditionalDrivers1BirthDate,
        minDate,
        formName: FormsEnum.CalculationForm,
      });
    }

    setSectionVisibilityHelper({
      key: SectionsEnum.AdditionalDrivers1,
      visible: hasAdditionalDrivers,
      formName: FormsEnum.CalculationForm,
    });
  },

  onInsuranceStartDateChange() {
    const { startDate } = getInsuranceData();
    const cepikData = getCepikDataFromSelector();
    const currentDate = moment().startOf('day');
    const insuranceStartDate = moment(startDate, DATE_FORMAT);
    const isPastDate = insuranceStartDate.isBefore(currentDate, 'day');
    const isCepikModal = window.location.pathname.includes(
      RouteEnum.COMPARE_CEPIK_DATA
    );
    const formName = isCepikModal
      ? FormsEnum.CompareCepikDataForm
      : FormsEnum.CalculationForm;

    const cepikStartDates = cepikData?.policyStartDate;

    if (cepikStartDates && cepikStartDates.length > 0 && !isCepikModal) {
      const maxCepikStartDate = moment().add(60, 'days');

      const cepikStartDate = cepikStartDates.find((date) => {
        const momentDate = moment(date, DATE_FORMAT).startOf('day');

        return (
          momentDate.isValid() &&
          !momentDate.isBefore(currentDate) &&
          !momentDate.isAfter(maxCepikStartDate)
        );
      });

      if (cepikStartDate) {
        const cepikPolicyStartDate = moment(cepikStartDate);
        const isInsuranceStartDateAfterCepikStartDate = insuranceStartDate.isAfter(
          cepikPolicyStartDate,
          'day'
        );

        if (isInsuranceStartDateAfterCepikStartDate) {
          setMessage({
            message: {
              content: QuestionMessagesEnum.CepikStartDateWarning.replace(
                'dd.mm.rrrr',
                cepikPolicyStartDate.format('DD.MM.YYYY')
              ),
              key: MessagesEnum.InsuranceStartDateAfterCepikPolicyStartDate,
              type: MessageTypeEnum.Warning,
              visible: true,
              questions: [FieldsEnum.InsuranceStartDate] as string[],
            },
            formName: FormsEnum.CalculationForm,
          });
        } else {
          setMessageVisibility({
            key: MessagesEnum.InsuranceStartDateAfterCepikPolicyStartDate,
            visible: false,
            formName: FormsEnum.CalculationForm,
          });
        }
      }
    }

    setMessageVisibility({
      key: MessagesEnum.InsurancePastDateNotAllowed,
      visible: isPastDate,
      formName,
    });

    const questionConfig = getQuestion(FieldsEnum.InsuranceStartDate, formName);
    const minDate = questionConfig?.minDate;
    const maxDate = questionConfig?.maxDate;
    const isDateValid =
      insuranceStartDate.isValid() &&
      isInValidRange(minDate, maxDate, startDate);

    setInitialFocusedDate({
      key: FieldsEnum.InsuranceStartDate,
      initialFocusedDate: startDate && isDateValid ? startDate : '',
      formName,
    });
  },

  onLeasedTypeChange() {
    const { leasingType } = getFormValues().insurant || {};

    const isLeasingTypeConsumer = leasingType === 'consumer';

    setQuestionVisibility({
      key: FieldsEnum.InsurantBusinessLegalStructure,
      visible: !isLeasingTypeConsumer,
      formName: FormsEnum.CalculationForm,
    });

    setQuestionVisibility({
      key: FieldsEnum.InsurantNip,
      visible: !isLeasingTypeConsumer,
      formName: FormsEnum.CalculationForm,
    });

    setQuestionLabel({
      key: FieldsEnum.OwnerHomeAddressZipCode,
      formName: FormsEnum.CalculationForm,
      label: isLeasingTypeConsumer
        ? ControlLabelEnum.ApartmentZipCodeRegistrationLabel
        : ControlLabelEnum.ApartmentZipCodeLabel,
    });

    setQuestionLabel({
      key: FieldsEnum.OwnerHomeAddressCity,
      formName: FormsEnum.CalculationForm,
      label: isLeasingTypeConsumer
        ? ControlLabelEnum.ApartmentCityRegistrationLabel
        : ControlLabelEnum.ApartmentCityLabel,
    });

    setQuestionHelperText({
      key: FieldsEnum.OwnerPesel,
      formName: FormsEnum.CalculationForm,
      helperText: isLeasingTypeConsumer ? '' : LabelEnum.FieldNotRequired,
    });

    setQuestionRequirability({
      key: FieldsEnum.OwnerPesel,
      formName: FormsEnum.CalculationForm,
      required: isLeasingTypeConsumer,
    });

    if (isLeasingTypeConsumer) {
      setQuestionVisibility({
        key: FieldsEnum.OwnerPesel,
        visible: true,
        formName: FormsEnum.CalculationForm,
      });

      store.dispatch(
        FormsConfigActions.setupValidatorForType({
          formName: FormsEnum.CalculationForm,
          questionKey: FieldsEnum.OwnerPesel,
        })
      );
    }
  },
  initializeCarLeaseCompany(retryCount = 0) {
    const leasingCompanyNip = getFormValue(FieldsEnum.LeasingCompanyNip);

    if (!leasingCompanyNip) {
      return;
    }

    const leasingCompaniesDictionary = getDictionaryFromSelector(
      FormsEnum.CalculationForm,
      `${FieldsEnum.CarLeaseCompany}_dictionary`
    );

    if (
      !leasingCompaniesDictionary ||
      leasingCompaniesDictionary.length === 0
    ) {
      if (retryCount < 5) {
        setTimeout(() => {
          BusinessFunctionsService.initializeCarLeaseCompany(retryCount + 1);
        }, 100);
      }
      return;
    }

    const foundCompany = leasingCompaniesDictionary.find(
      (company) => company.key === leasingCompanyNip
    );

    if (foundCompany) {
      changeFieldValue(
        FieldsEnum.CarLeaseCompany,
        foundCompany.key,
        FormsEnum.CalculationForm
      );
    } else {
      const otherCompany = leasingCompaniesDictionary.find(
        (company) => company.key === '0000000000'
      );

      setSectionVisibilityHelper({
        key: SectionsEnum.MubiLeasingSectionLeasingCompany,
        visible: true,
        formName: FormsEnum.CalculationForm,
      });

      if (otherCompany) {
        changeFieldValue(
          FieldsEnum.CarLeaseCompany,
          otherCompany.key,
          FormsEnum.CalculationForm
        );
      }
    }
  },

  onCarLeaseCompanyChange() {
    const carLeaseCompanyNip = getFormValue(FieldsEnum.CarLeaseCompany);

    if (carLeaseCompanyNip === '0000000000') {
      setSectionVisibilityHelper({
        key: SectionsEnum.MubiLeasingSectionLeasingCompany,
        visible: true,
        formName: FormsEnum.CalculationForm,
      });
      clearFieldValue(FieldsEnum.LeasingCompanyNip, FormsEnum.CalculationForm);
      clearFieldValue(FieldsEnum.LeasingCompanyName, FormsEnum.CalculationForm);
      return;
    } else {
      setSectionVisibilityHelper({
        key: SectionsEnum.MubiLeasingSectionLeasingCompany,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
    }

    const leasingCompaniesDictionary = getDictionaryFromSelector(
      FormsEnum.CalculationForm,
      `${FieldsEnum.CarLeaseCompany}_dictionary`
    );

    const foundCompany = leasingCompaniesDictionary.find(
      (company) => company.key === carLeaseCompanyNip
    );

    if (foundCompany) {
      changeFieldValueAndUpdate(
        FieldsEnum.LeasingCompanyName,
        foundCompany.value,
        FormsEnum.CalculationForm
      );

      changeFieldValueAndUpdate(
        FieldsEnum.LeasingCompanyNip,
        foundCompany.key,
        FormsEnum.CalculationForm
      );
    }
  },

  async onLeasedStatusChange(initialRun: boolean) {
    const { leased } = getFormValues().legalStatus;
    const { vehicleType, legalStatus, insurant } = getQuotation();
    const valueChanged = leased !== legalStatus?.leased;
    const isMotorcycle = vehicleType === VehicleTypeEnum.Motorcycle;
    const isFromRenewal = getQuotationIsFromRenewal();

    if (isFromRenewal) {
      changeQuestionClearOnHide(
        FieldsEnum.LegalStatusLeased,
        FormsEnum.CalculationForm,
        false
      );

      setQuestionVisibility({
        key: FieldsEnum.LegalStatusLeased,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
    } else {
      setMessageVisibility({
        key: MessagesEnum.NoInsuranceForVehicleInLeasing,
        visible: isMubiLeasingActive ? leased && isMotorcycle : leased,
        formName: FormsEnum.CalculationForm,
      });

      if (isMubiLeasingActive) {
        const currentOverwrittenConfig = store.getState().formsConfig
          .overwrittenFormConfig;

        const isFormMicroCenterActive = selectIsFormMicroCenterActive(
          store.getState()
        );

        const hideShortPathIfCepik = (config: FormConfig) => {
          config.screens.forEach((screen) => {
            screen.sections.forEach((section) => {
              if (
                section.key === SectionsEnum.ShortCalculationPath &&
                isFormMicroCenterActive
              ) {
                section.visible = false;
              }
            });
          });

          return config;
        };

        const updatedConfig = hideShortPathIfCepik(
          await updateFormConfigWithMjmConsent(
            updateFormConfigWithConsentLabels(
              currentOverwrittenConfig.previousConfig,
              mubiLeasingFormConfig
            )
          )
        );

        if (!isMotorcycle && leased) {
          if (initialRun) {
            store.dispatch(
              FormsConfigActions.setOverwrittenFormConfigAction({
                ...currentOverwrittenConfig,
                config: updatedConfig,
                shouldUpdate: true,
              })
            );
          }

          setTimeout(() => {
            BusinessFunctionsService.onLeasingVehicleTypeChange();
            BusinessFunctionsService.onProductionYearChange();
            BusinessFunctionsService.onLeasedTypeChange();
            BusinessFunctionsService.initializeCarLeaseCompany();
            BusinessFunctionsService.registrationNumberNotKnownChange();

            if (!insurant || insurant.businessLegalStructure === null) {
              changeFieldValueAndUpdate(
                FieldsEnum.InsurantBusinessLegalStructure,
                'sole_proprietorship'
              );
            }

            const currentInsurant = getFormValues().insurant;
            if (currentInsurant?.leasingType !== 'consumer') {
              setQuestionVisibility({
                key: FieldsEnum.OwnerPesel,
                visible:
                  currentInsurant?.businessLegalStructure ===
                  'sole_proprietorship',
                formName: FormsEnum.CalculationForm,
              });
            }

            setMessageVisibility({
              key: MessagesEnum.LeasedCarInfo,
              visible: true,
              formName: FormsEnum.CalculationForm,
            });
          }, 0);
        } else {
          setMessageVisibility({
            key: MessagesEnum.LeasedCarInfo,
            visible: false,
            formName: FormsEnum.CalculationForm,
          });
        }

        if (isMotorcycle || !valueChanged || initialRun) return;

        if (valueChanged) {
          changeFieldValueAndUpdate(FieldsEnum.LegalStatusLeased, leased);
        }

        const desiredConfig = leased ? updatedConfig : ({} as FormConfig);

        if (
          desiredConfig?.screens?.[0]?.key !==
          currentOverwrittenConfig.config?.screens?.[0]?.key
        ) {
          store.dispatch(
            FormsConfigActions.setOverwrittenFormConfigAction({
              ...currentOverwrittenConfig,
              config: desiredConfig,
              shouldUpdate: true,
            })
          );
        }
      }
    }
  },

  onFirstRegistrationDateChange() {
    const isMubiLeasingConfig = getIsMubiLeasingConfig();

    setTimeout(() => showRegistrationDateMessages(), 0);

    const firstRegistrationDate = get(
      getFormValues(),
      FieldsEnum.CarFirstRegistrationDate
    );

    const { minDate, maxDate } = getQuestion(
      FieldsEnum.CarFirstRegistrationDate
    );

    const isValidFirstRegistrationDate =
      firstRegistrationDate &&
      isInValidRange(minDate, maxDate, firstRegistrationDate);

    if (!isMubiLeasingConfig) {
      setQuestionAvailability({
        key: FieldsEnum.CarOwnerPurchaseYear,
        enabled: isValidFirstRegistrationDate,
        formName: FormsEnum.CalculationForm,
      });
    }
  },

  setOwnerLicenseMonthVisibility() {
    const isFromRenewal = getQuotationIsFromRenewal();
    const isDataOwnerChangedRenewal = getIsDataOwnersChangedRenewal('owner');

    if (isFromRenewal && !isDataOwnerChangedRenewal) {
      changeQuestionClearOnHide(
        FieldsEnum.OwnerDriverLicenseMonth,
        FormsEnum.CalculationForm,
        false
      );
      changeQuestionClearOnHide(
        FieldsEnum.OwnerDriverLicenseYear,
        FormsEnum.CalculationForm,
        false
      );
      changeQuestionClearOnHide(
        FieldsEnum.OwnerNoDrivingLicense,
        FormsEnum.CalculationForm,
        false
      );
      setQuestionVisibility({
        key: FieldsEnum.OwnerDriverLicenseMonth,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
      setQuestionVisibility({
        key: FieldsEnum.OwnerDriverLicenseYear,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
      setQuestionVisibility({
        key: FieldsEnum.OwnerNoDrivingLicense,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
    } else {
      setLicenseMonthVisibility(
        FieldsEnum.OwnerBirthDate,
        FieldsEnum.OwnerDriverLicenseYear,
        FieldsEnum.OwnerDriverLicenseMonth,
        FieldsEnum.OwnerNoDrivingLicense
      );
    }
  },

  setFirstCoownerLicenseMonthVisibility() {
    const isFromRenewal = getQuotationIsFromRenewal();
    const isDataFirstCoOwnerChangedRenewal = getIsDataOwnersChangedRenewal(
      'firstCoOwner'
    );

    if (isFromRenewal && !isDataFirstCoOwnerChangedRenewal) {
      changeQuestionClearOnHide(
        FieldsEnum.Coowners1LicenseYear,
        FormsEnum.CalculationForm,
        false
      );
      changeQuestionClearOnHide(
        FieldsEnum.Coowners1LicenseMonth,
        FormsEnum.CalculationForm,
        false
      );
      changeQuestionClearOnHide(
        FieldsEnum.Coowners1NoDrivingLicense,
        FormsEnum.CalculationForm,
        false
      );
      setQuestionVisibility({
        key: FieldsEnum.Coowners1LicenseYear,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
      setQuestionVisibility({
        key: FieldsEnum.Coowners1LicenseMonth,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
      setQuestionVisibility({
        key: FieldsEnum.Coowners1NoDrivingLicense,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
    } else {
      setLicenseMonthVisibility(
        FieldsEnum.Coowners1BirthDate,
        FieldsEnum.Coowners1LicenseYear,
        FieldsEnum.Coowners1LicenseMonth,
        FieldsEnum.Coowners1NoDrivingLicense
      );
    }
  },

  setSecondCoownerLicenseMonthVisibility() {
    const isFromRenewal = getQuotationIsFromRenewal();
    const isDataSecondCoOwnerChangedRenewal = getIsDataOwnersChangedRenewal(
      'secondCoOwner'
    );

    if (isFromRenewal && !isDataSecondCoOwnerChangedRenewal) {
      changeQuestionClearOnHide(
        FieldsEnum.Coowners2LicenseYear,
        FormsEnum.CalculationForm,
        false
      );
      changeQuestionClearOnHide(
        FieldsEnum.Coowners2LicenseMonth,
        FormsEnum.CalculationForm,
        false
      );
      changeQuestionClearOnHide(
        FieldsEnum.Coowners2NoDrivingLicense,
        FormsEnum.CalculationForm,
        false
      );
      setQuestionVisibility({
        key: FieldsEnum.Coowners2LicenseYear,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
      setQuestionVisibility({
        key: FieldsEnum.Coowners2LicenseMonth,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
      setQuestionVisibility({
        key: FieldsEnum.Coowners2NoDrivingLicense,
        visible: false,
        formName: FormsEnum.CalculationForm,
      });
    } else {
      setLicenseMonthVisibility(
        FieldsEnum.Coowners2BirthDate,
        FieldsEnum.Coowners2LicenseYear,
        FieldsEnum.Coowners2LicenseMonth,
        FieldsEnum.Coowners2NoDrivingLicense
      );
    }
  },

  setFirstAdditionalDriverLicenseMonthVisibility() {
    setLicenseMonthVisibility(
      FieldsEnum.AdditionalDrivers1BirthDate,
      FieldsEnum.AdditionalDrivers1LicenseYear,
      FieldsEnum.AdditionalDrivers1LicenseMonth,
      undefined
    );
  },

  mainOwnerBirthDateChanged() {
    const formValues = getFormValues(FormsEnum.CalculationForm);
    const birthDate = get(formValues, FieldsEnum.OwnerBirthDate);

    if (!birthDate) {
      setMessageVisibility({
        formName: FormsEnum.CalculationForm,
        key: getMessageKeyWithSuffix(FieldsEnum.OwnerBirthDate, 'invalid_date'),
        visible: false,
      });
      setMessageVisibility({
        formName: FormsEnum.CalculationForm,
        key: getMessageKeyWithSuffix(
          FieldsEnum.OwnerBirthDate,
          'too_young_owner'
        ),
        visible: false,
      });
    }
  },

  setupOwnerLicenseDates(initialRun: boolean) {
    setupLicenseDates(
      FieldsEnum.OwnerDriverLicenseMonth,
      FieldsEnum.OwnerDriverLicenseYear,
      FieldsEnum.OwnerBirthDate,
      FieldsEnum.OwnerNoDrivingLicense
    );
    setupNoDrivingLicenseFieldVisibility(
      FieldsEnum.OwnerNoDrivingLicense,
      ModelPathEnum.OwnerNoDriverLicense,
      FieldsEnum.OwnerDriverLicenseYear,
      FieldsEnum.OwnerBirthDate,
      initialRun
    );
  },

  setupFirstCoownerLicenseDates(initialRun: boolean) {
    setupLicenseDates(
      FieldsEnum.Coowners1LicenseMonth,
      FieldsEnum.Coowners1LicenseYear,
      FieldsEnum.Coowners1BirthDate,
      FieldsEnum.Coowners1NoDrivingLicense
    );
    setupNoDrivingLicenseFieldVisibility(
      FieldsEnum.Coowners1NoDrivingLicense,
      ModelPathEnum.Coowners1NoDriverLicense,
      FieldsEnum.Coowners1LicenseYear,
      FieldsEnum.Coowners1BirthDate,
      initialRun
    );
  },

  setupSecondCoownerLicenseDates(initialRun: boolean) {
    setupLicenseDates(
      FieldsEnum.Coowners2LicenseMonth,
      FieldsEnum.Coowners2LicenseYear,
      FieldsEnum.Coowners2BirthDate,
      FieldsEnum.Coowners2NoDrivingLicense
    );
    setupNoDrivingLicenseFieldVisibility(
      FieldsEnum.Coowners2NoDrivingLicense,
      ModelPathEnum.Coowners2NoDriverLicense,
      FieldsEnum.Coowners2LicenseYear,
      FieldsEnum.Coowners2BirthDate,
      initialRun
    );
  },

  setupFirstAdditionalDriverLicenseDates() {
    setupLicenseDates(
      FieldsEnum.AdditionalDrivers1LicenseMonth,
      FieldsEnum.AdditionalDrivers1LicenseYear,
      FieldsEnum.AdditionalDrivers1BirthDate,
      undefined
    );
  },

  onLeasingVehicleTypeChange() {
    const leasingVehicleType = get(
      getFormValues(),
      FieldsEnum.CarLeasingVehicleType
    );

    if (!leasingVehicleType) return;

    setMessageVisibility({
      formName: FormsEnum.CalculationForm,
      key: MessagesEnum.NoInsuranceForThisVehicleType,
      visible: leasingVehicleType === LeasingVehicleTypeEnum.Other,
    });
  },

  onInsurantBusinessLegalStructureChange() {
    const businessLegalStructure = getFormValues().insurant
      .businessLegalStructure;

    setQuestionVisibility({
      key: FieldsEnum.OwnerPesel,
      visible: businessLegalStructure === 'sole_proprietorship',
      formName: FormsEnum.CalculationForm,
    });
  },
};

export function setNoDriverLicenseFieldsVisibility(
  type: DriverType.OWNER | DriverType.COOWNERS_1 | DriverType.COOWNERS_2,
  initialRun?: boolean
) {
  const getDataFunction = {
    [DriverType.OWNER]: getOwnerData,
    [DriverType.COOWNERS_1]: getFirstCoownerData,
    [DriverType.COOWNERS_2]: getSecondCoownerData,
  }[type];

  const noDriverLicenseField = {
    [DriverType.OWNER]: FieldsEnum.OwnerNoDrivingLicense,
    [DriverType.COOWNERS_1]: FieldsEnum.Coowners1NoDrivingLicense,
    [DriverType.COOWNERS_2]: FieldsEnum.Coowners2NoDrivingLicense,
  }[type];

  const { birthDate } = getDataFunction();
  const currentDate = moment();
  const yearsDiff = currentDate.diff(moment(birthDate), 'years');

  if (yearsDiff < 17) {
    setQuestionVisibility({
      key: noDriverLicenseField,
      visible: false,
      formName: FormsEnum.CalculationForm,
    });
  }
}

export function runBusinessFunction(functionName: string, initialRun: boolean) {
  const businessFunction = BusinessFunctionsService[functionName];
  const cepikModalSavingInProgress = store.getState().shortPathData
    .isCepikModalSavingInProgress;

  if (cepikModalSavingInProgress) return;

  if (!businessFunction) {
    console.warn(`Unimplemented business function: ${functionName}`);
    return;
  } else {
    console.info(`Run business function: ${functionName}`);
  }

  businessFunction(initialRun);
}
